import React from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useIsNarrow } from "./hooks";

interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  body?: React.ReactNode;
  confirmLabel: string;
  tone?: "default" | "danger" | "money";
  /** Ask for a typed reason (sent to onConfirm); required when true */
  reasonLabel?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/** "Are you sure?" with an optional required reason. Money actions use the
 * coral button; destructive ones the danger colour. */
export default function ConfirmDialog({ open, title, body, confirmLabel, tone = "default", reasonLabel, busy, error, onConfirm, onClose }: ConfirmDialogProps) {
  const [reason, setReason] = React.useState("");
  const narrow = useIsNarrow(600);
  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);
  const needsReason = !!reasonLabel;
  const disabled = busy || (needsReason && !reason.trim());
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={false} PaperProps={{ sx: { m: narrow ? 2 : 4 } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {body && (typeof body === "string" ? <Typography variant="body2">{body}</Typography> : body)}
        {needsReason && (
          <TextField label={reasonLabel} value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} autoFocus fullWidth />
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={tone === "danger" ? "error" : tone === "money" ? "secondary" : "primary"}
          onClick={() => onConfirm(reason.trim())}
          disabled={disabled}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
